import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, PackageCheck } from "lucide-react";
import { useAdmin } from "../admin-context";

const currency = (value) => `$${Number(value || 0).toFixed(2)}`;

const getStatusColor = (status) => {
  switch (status) {
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "confirmed":
      return "bg-purple-100 text-purple-800";
    case "shipped":
      return "bg-blue-100 text-blue-800";
    case "delivered":
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const OrderViewPage = () => {
  const { orderId } = useParams();
  const { getOrderById, updateOrderStatus } = useAdmin();
  const order = getOrderById(orderId);

  if (!order) {
    return (
      <div className="space-y-6">
        <Link
          className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 hover:text-orange-700"
          to="/admin/orders"
        >
          <ArrowLeft size={16} />
          Back to Orders
        </Link>

        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-50 text-orange-500">
            <PackageCheck size={26} />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Order not found</h1>
          <p className="mt-2 text-sm text-gray-500">
            We could not find order #{orderId}. Try returning to the orders list.
          </p>
        </div>
      </div>
    );
  }

  const items = order.items || [];
  const total = items.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
    0
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <Link
            className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 hover:text-orange-700"
            to="/admin/orders"
          >
            <ArrowLeft size={16} />
            Back to Orders
          </Link>
          <h1 className="mt-3 text-3xl font-bold text-gray-900">Order #{order.id}</h1>
          <p className="mt-1 text-sm text-gray-500">
            Review the purchased items and update the fulfillment status
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <span className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${getStatusColor(order.status)}`}>
            {order.status || "pending"}
          </span>
          <select
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium outline-none focus:border-orange-500"
            onChange={(event) => updateOrderStatus(order.id, event.target.value)}
            value={order.status || "pending"}
          >
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
          </select>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-500">Order Date</p>
          <p className="mt-2 text-lg font-bold text-gray-900">
            {order.date || order.created_at || "Not available"}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-500">Items</p>
          <p className="mt-2 text-lg font-bold text-gray-900">{items.length}</p>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-500">Order Total</p>
          <p className="mt-2 text-lg font-bold text-gray-900">{currency(total)}</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left whitespace-nowrap">
            <thead className="border-b bg-gray-50">
              <tr>
                <th className="p-4 font-bold text-gray-600">Product</th>
                <th className="p-4 font-bold text-gray-600">Category</th>
                <th className="p-4 font-bold text-gray-600">Quantity</th>
                <th className="p-4 font-bold text-gray-600">Unit Price</th>
                <th className="p-4 font-bold text-gray-600">Line Total</th>
                <th className="p-4 font-bold text-gray-600 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((orderItem, index) => {
                const product = orderItem.item || {};
                const image = product.images?.image;
                const quantity = Number(orderItem.quantity || 0);
                const unitPrice = Number(orderItem.price || 0);
                const lineTotal = quantity * unitPrice;

                return (
                  <tr key={`${orderItem.menu_item_id || index}-${index}`} className="transition hover:bg-gray-50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded border bg-white p-1">
                          {image ? (
                            <img alt={product.name || "Ordered item"} className="h-full w-full object-contain" src={image} />
                          ) : (
                            <span className="text-xs font-bold text-gray-400">No Img</span>
                          )}
                        </div>
                        <div className="max-w-[220px]">
                          <p className="truncate font-bold text-gray-900">
                            {product.name || `Menu Item #${orderItem.menu_item_id || index + 1}`}
                          </p>
                          <p className="truncate text-xs text-gray-400">
                            SKU #{orderItem.menu_item_id || "N/A"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="rounded bg-gray-100 px-2 py-1 text-xs font-bold uppercase">
                        {product.category?.name || "Uncategorized"}
                      </span>
                    </td>
                    <td className="p-4 font-semibold text-gray-700">{quantity}</td>
                    <td className="p-4 font-semibold text-gray-700">{currency(unitPrice)}</td>
                    <td className="p-4 font-bold text-gray-900">{currency(lineTotal)}</td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        {image ? (
                          <a
                            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
                            href={image}
                            rel="noreferrer"
                            target="_blank"
                          >
                            Preview
                            <ExternalLink size={14} />
                          </a>
                        ) : (
                          <span className="inline-flex items-center rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-400">
                            No Preview
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrderViewPage;
